import React from "react";
import { WorkOrderWithAppointments } from "@myapp/contract";
import { formatDateDDMMYYYY } from "../../../../shared/helpers/date";
import { ListItem } from "../../../../shared/ui/list/ListIteam/ListItem";
import { workOrderColumns } from "../model/constants";
import { getWorkOrderStatusColor, getWorkOrderStatusLabel } from "../../../../entities/work-order";
import { routes } from "../../../../shared/config/routes";
import { Link } from "react-router-dom";

interface Props {
  item: WorkOrderWithAppointments;
}

const formatValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

export const WorkOrderListItem: React.FC<Props> = ({ item }) => {
  const technicians = item.appointments
    .map(a => a.technician)
    .filter((t, i, arr) => t && arr.findIndex(x => x?.id === t.id) === i);
  const detailLink = item.external.auftragNr
    ? routes.task.detail(`${item.external.auftragJahr}${item.external.auftragNr}`)
    : undefined;

  const id = formatValue(item.workOrderId ?? `${item.external.auftragJahr}${item.external.auftragNr}`);
  const date = item.external.auftragsdatum ? formatDateDDMMYYYY(item.external.auftragsdatum) : "-";

  return (
    <>
      {/* ── Mobile card (<768px) ── */}
      <div className="md:hidden">
        <Link
          to={detailLink ?? "#"}
          className="block w-full rounded-2xl bg-white px-4 py-3 active:opacity-80"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-['Roboto'] text-[13px] font-bold text-primary">#{id}</span>
            <div className="flex items-center gap-1.5">
              <span className="font-['Roboto'] text-[12px] text-gray-500">
                {getWorkOrderStatusLabel(item.status)}
              </span>
              <div
                className="h-3.5 w-3.5 shrink-0 rounded-full"
                style={{ backgroundColor: getWorkOrderStatusColor(item.status) }}
              />
            </div>
          </div>

          <p className="mt-1 font-['Roboto'] text-[14px] font-bold text-gray-800 leading-tight">
            {formatValue(item.external.mieter.name)}
          </p>

          {technicians.length > 0 && (
            <p className="mt-0.5 font-['Roboto'] text-[12px] text-gray-500">
              {technicians.map(t => t?.name).filter(Boolean).join(", ")}
            </p>
          )}

          {item.external.gewerkeText && (
            <p className="mt-0.5 font-['Roboto'] text-[12px] text-gray-500">
              {item.external.gewerkeText}
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 font-['Roboto'] text-[12px] text-gray-400">
            <span>{date}</span>
            {item.external.veAdresse && <span>{item.external.veAdresse}</span>}
            {item.external.hauseingang && (
              <span>
                {item.external.hauseingang}
                {item.external.wohnung ? ` · ${item.external.wohnung}` : ""}
              </span>
            )}
          </div>
        </Link>
      </div>

      {/* ── Desktop / tablet row (≥768px) ── */}
      <div className="hidden md:block w-full">
        <ListItem columns={workOrderColumns} link={detailLink}>
          <ListItem.Cell>{id}</ListItem.Cell>
          <ListItem.Cell>{formatValue(item.external.mieter.name)}</ListItem.Cell>
          <ListItem.Cell>
            {technicians.map((t, i) => (
              <React.Fragment key={i}>
                {t?.name}
                <br />
              </React.Fragment>
            ))}
          </ListItem.Cell>
          <ListItem.Cell>{formatValue(item.external.gewerkeText)}</ListItem.Cell>
          <ListItem.Cell>{date}</ListItem.Cell>
          <ListItem.Cell>{formatValue(item.external.veAdresse)}</ListItem.Cell>
          <ListItem.Cell>{formatValue(item.external.hauseingang)}</ListItem.Cell>
          <ListItem.Cell>{formatValue(item.external.wohnung)}</ListItem.Cell>
          <ListItem.Cell className="!flex flex-row items-center justify-between">
            <div>{getWorkOrderStatusLabel(item.status)}</div>
            <div
              className="h-6 w-6 rounded-full"
              style={{ backgroundColor: getWorkOrderStatusColor(item.status) }}
            />
          </ListItem.Cell>
        </ListItem>
      </div>
    </>
  );
};
